import React, { useCallback } from "react";
import { Close, WarningAmber } from "@mui/icons-material";
import {
  alpha,
  Button,
  ListItemButton as MuiListItemButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { treeItemClasses } from "@mui/x-tree-view";
import { CopyButton } from "~/components/CopyableText";
import { JsonTree } from "~/components/JsonTree";
import { ErrorMessage } from "~/components/error-message";
import { useResizeObserver } from "~/lib/resize-observer";
import { get } from "~/lib/std";
import { getEventHandlerProps } from "~/utils/get-event-handler-props";
import { usePlayerActions } from "../../actions";
import { LoadingFeedback, PanelLayout } from "../../components";
import type { TerminalPanel } from "../../panels";
import { usePairedTopics } from "../../panels";
import { useFormatPlaybackTimestamp } from "../../playback";
import type { PlayerRecord } from "../../record-store";
import type { TerminalData } from "./use-terminal-records";
import { useTerminalRecords } from "./use-terminal-records";
import { VirtualizedList } from "./virtualized-list";

const Root = styled("div")({
  width: "100%",
  height: "100%",
  display: "grid",
  gridTemplateAreas: '"header details" "list details"',
  gridTemplateRows: "auto minmax(0, 1fr)",
});

const NO_RECORD_SELECTED_COLUMNS = "minmax(0, 1fr) 0";
const RECORD_SELECTED_COLUMNS = "repeat(2, minmax(0, 1fr))";

const HeaderArea = styled("div")(({ theme }) => ({
  gridArea: "header",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  columnGap: theme.spacing(1),
  padding: theme.spacing(1),
  borderBlockEnd: `1px solid ${theme.palette.divider}`,
}));

const ListArea = styled("div")(({ theme }) => ({
  gridArea: "list",
  "--bg-most-recent": alpha(theme.palette.success.main, 0.2),
}));

const DetailsArea = styled("div")(({ theme }) => ({
  gridArea: "details",
  overflowY: "auto",
  borderInlineStart: `1px solid ${theme.palette.divider}`,
}));

const ListItemButton = styled(MuiListItemButton)(({ theme }) => ({
  margin: 0,
  paddingInline: 0,
  paddingBlock: theme.spacing(0.5),
  display: "block",
  fontFamily: "monospace",
  wordBreak: "break-all",
  // Don't want background color transitioning during playback.
  transition: "none",
}));

const Timestamp = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: "bold",
}));

const Data = styled("span")(({ theme }) => ({
  ...theme.typography.body2,
  fontFamily: "monospace",
}));

export function TerminalVisualization({ panel }: { panel: TerminalPanel }) {
  // Ensure the width change caused by toggling the record details section is
  // flushed in the same frame as the toggle action. Otherwise, list item
  // positions will be incorrect for a frame because the actual width determined
  // by the grid layout will be in effect but the stored width as measured by
  // the ResizeObserver's update will not yet be flushed.
  const { ref, width, height } = useResizeObserver({ sync: true });

  const [terminalRecordsSnapshot, isPlaceholderSnapshot] = useTerminalRecords({
    panel,
  });

  const [pair] = usePairedTopics(panel);

  const playerActions = usePlayerActions();

  let content;
  if (terminalRecordsSnapshot.status === "pending") {
    content = <LoadingFeedback description="data" />;
  } else if (terminalRecordsSnapshot.status === "rejected") {
    content = (
      <ErrorMessage>An error occurred. Couldn't get terminal data</ErrorMessage>
    );
  } else {
    const { value: terminalData } = terminalRecordsSnapshot;

    const clearFieldHandlerProps = getEventHandlerProps(
      "onClick",
      terminalData.field != null &&
        function handleClearField(): void {
          playerActions.clearTerminalField(panel, pair.topic);
        },
    );

    content = (
      <>
        <Root
          key={terminalData.topicId}
          style={{
            gridTemplateColumns:
              terminalData.selectedRecord == null
                ? NO_RECORD_SELECTED_COLUMNS
                : RECORD_SELECTED_COLUMNS,
          }}
        >
          <HeaderArea>
            <Typography
              noWrap
              sx={
                terminalData.field == null ? { fontStyle: "italic" } : undefined
              }
            >
              {terminalData.field == null
                ? "No field selected"
                : terminalData.field}
            </Typography>
            <Button
              color="primary"
              variant="text"
              size="small"
              {...clearFieldHandlerProps}
              sx={{ flex: "none" }}
            >
              Clear Field
            </Button>
          </HeaderArea>
          <ListArea ref={ref}>
            {width != null && height != null && (
              <RecordList
                panel={panel}
                width={width}
                height={height}
                terminalData={terminalData}
              />
            )}
          </ListArea>
          <DetailsArea>
            <RecordDetails panel={panel} terminalData={terminalData} />
          </DetailsArea>
        </Root>
        {isPlaceholderSnapshot && <LoadingFeedback description="data" />}
      </>
    );
  }

  return <PanelLayout>{content}</PanelLayout>;
}

function RecordList({
  panel,
  width,
  height,
  terminalData,
}: {
  panel: TerminalPanel;
  width: number;
  height: number;
  terminalData: TerminalData;
}) {
  const [pair] = usePairedTopics(panel);

  const playerActions = usePlayerActions();

  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  function createSeekHandler(record: PlayerRecord<"default">) {
    return function handleSeek(): void {
      playerActions.seek(record.timestamp);
    };
  }

  function createRecordClickHandler(record: PlayerRecord<"default">) {
    return function handleRecordClick(e: React.MouseEvent): void {
      playerActions.toggleSelectedTerminalRecord(panel, pair.topic, record, e);
    };
  }

  const firstSetRecordTimestamp = terminalData.records.at(0)!.timestamp;
  const lastSetRecordTimestamp = terminalData.records.at(-1)!.timestamp;
  const { firstTopicRecordTimestamp, lastTopicRecordTimestamp } = terminalData;

  const getItemKey = useCallback(
    ({ timestamp }: PlayerRecord<"default">): React.Key => {
      if (
        timestamp === firstSetRecordTimestamp &&
        timestamp !== firstTopicRecordTimestamp
      ) {
        return "more-behind";
      } else if (
        timestamp === lastSetRecordTimestamp &&
        timestamp !== lastTopicRecordTimestamp
      ) {
        return "more-ahead";
      } else {
        return timestamp;
      }
    },
    [
      firstSetRecordTimestamp,
      firstTopicRecordTimestamp,
      lastSetRecordTimestamp,
      lastTopicRecordTimestamp,
    ],
  );

  const fieldPath = terminalData.field?.split(".") ?? null;

  function getListItemData(record: PlayerRecord<"default">): React.ReactNode {
    const selectedData =
      fieldPath == null ? record.data : get(record.data, fieldPath);

    if (selectedData == null) {
      return null;
    } else {
      return ` ${JSON.stringify(selectedData)}`;
    }
  }

  return (
    <VirtualizedList
      key={terminalData.field}
      width={width}
      height={height}
      items={terminalData.records}
      {...(panel.autoScroll && {
        pinnedItemIndex: terminalData.mostRecentRecordIndex,
        // Whenever the playback timestamp changes the most recent record
        // should be re-pinned to the bottom of the scrollport.
        repinKey: terminalData.timestamp,
      })}
      prerenderCount={10}
      getItemKey={getItemKey}
      renderItem={(record, index) => {
        const isFirstInSet = record.timestamp === firstSetRecordTimestamp;
        const isFirstInTopic = record.timestamp === firstTopicRecordTimestamp;

        const isLastInSet = record.timestamp === lastSetRecordTimestamp;
        const isLastInTopic = record.timestamp === lastTopicRecordTimestamp;

        const isMostRecent = index === terminalData.mostRecentRecordIndex;

        const isSelected =
          record.timestamp === terminalData.selectedRecord?.timestamp;

        return (
          <>
            {isFirstInSet && !isFirstInTopic && (
              <Stack spacing={1} sx={{ alignItems: "center", marginBlock: 2 }}>
                <Typography>More records available</Typography>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={createSeekHandler(record)}
                >
                  Seek to {formatPlaybackTimestamp(record.timestamp)}
                </Button>
              </Stack>
            )}
            <ListItemButton
              selected={isSelected}
              onClick={createRecordClickHandler(record)}
              style={{
                backgroundColor:
                  isMostRecent && !isSelected
                    ? "var(--bg-most-recent)"
                    : undefined,
              }}
            >
              <Timestamp>{formatPlaybackTimestamp(record.timestamp)}</Timestamp>
              <Data>{getListItemData(record)}</Data>
            </ListItemButton>
            {isLastInSet && !isLastInTopic && (
              <Stack spacing={1} sx={{ alignItems: "center", marginBlock: 2 }}>
                <Typography>More records available</Typography>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={createSeekHandler(record)}
                >
                  Seek to {formatPlaybackTimestamp(record.timestamp)}
                </Button>
              </Stack>
            )}
          </>
        );
      }}
    />
  );
}

function RecordDetails({
  panel,
  terminalData,
}: {
  panel: TerminalPanel;
  terminalData: TerminalData;
}) {
  const [pair] = usePairedTopics(panel);

  const playerActions = usePlayerActions();

  const formatPlaybackTimestamp = useFormatPlaybackTimestamp();

  const { records, selectedRecord } = terminalData;

  if (selectedRecord == null) {
    return null;
  }

  const isOutsideListRange =
    selectedRecord.timestamp < records.at(0)!.timestamp ||
    selectedRecord.timestamp > records.at(-1)!.timestamp;

  const handleClose = (): void => {
    playerActions.clearSelectedTerminalRecord(panel, pair.topic);
  };

  const handleSeek = (): void => {
    playerActions.seek(selectedRecord.timestamp);
  };

  function handleTreeItemToggle(e: React.SyntheticEvent): false | void {
    if (!checkEventOriginatedFrom(e, treeItemClasses.iconContainer)) {
      return false;
    }
  }

  const handleSetField = (path: string, e: React.SyntheticEvent): void => {
    if (checkEventOriginatedFrom(e, treeItemClasses.iconContainer)) {
      return;
    }

    playerActions.setTerminalField(panel, pair.topic, path);
  };

  return (
    <Stack spacing={2} sx={{ padding: 1 }}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
          <Typography variant="h6" component="p">
            Record Details
          </Typography>
          {isOutsideListRange && (
            <Tooltip title="This record is outside the current list range">
              <WarningAmber fontSize="small" color="warning" />
            </Tooltip>
          )}
        </Stack>
        <Button
          color="primary"
          variant="text"
          onClick={handleClose}
          startIcon={<Close />}
        >
          Close
        </Button>
      </Stack>
      <div>
        <Typography sx={{ fontWeight: "bold", color: "text.secondary" }}>
          Timestamp
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Typography>{String(selectedRecord.timestamp)}</Typography>
          <CopyButton text={String(selectedRecord.timestamp)} size="small" />
        </Stack>
        <Button
          color="primary"
          variant="outlined"
          size="small"
          onClick={handleSeek}
          sx={{ marginBlockStart: 1 }}
        >
          Seek to {formatPlaybackTimestamp(selectedRecord.timestamp)}
        </Button>
      </div>
      <div>
        <Typography
          sx={{ fontWeight: "bold", color: "text.secondary" }}
          gutterBottom
        >
          Query Data
        </Typography>
        <JsonTree
          src={selectedRecord.data}
          selected={terminalData.field}
          onToggle={handleTreeItemToggle}
          onSelect={handleSetField}
        />
      </div>
    </Stack>
  );
}

function checkEventOriginatedFrom(
  e: React.SyntheticEvent,
  className: string,
): boolean {
  const element = e.target as Element;

  const selector = `.${className}`;

  return element.matches(selector) || element.closest(selector) != null;
}
